import { Recording } from '@streem/sdk-core';
import { StreemshotId } from '@streem/domain-id';
import { useMediaBreakpoint, useObservable, TimeStampController } from '@streem/sdk-react';
import { AppIcon, AppText, Box, Row, Skeleton, styled, Tooltip, useTheme } from '@streem/ui-react';
import {
    FC,
    KeyboardEvent as ReactKeyboardEvent,
    useEffect,
    useMemo,
    useState,
    useRef,
    CSSProperties,
    MutableRefObject,
    MouseEventHandler,
} from 'react';
import { useHistory } from 'react-router-dom';
import { WallItem } from 'streem-sdk-protobuf';
import { useDetailSession } from '../../hooks/detail_session_hooks';
import { StreemshotData, useFetchStreemshotData } from '../../hooks/use_fetch_streemshot_data';
import { joinPaths } from '../../util/routing';
import appLogger from '../../util/logging/app_logger';
import { useTimeStampController } from '../../hooks/use_timestamp_controller';
import { WallItemSubtype } from '@streem/sdk-types';
import { useTranscriptionContext } from '../../hooks/use_transcription_context';

const log = appLogger.extend('recording thumbnail');
type StreemshotWithRevision = StreemshotData &
    WallItemSubtype<WallItem, 'streemshot'> &
    Partial<WallItem['streemshotRevision']>;
export type OnClickTimeStamp = (
    timestamp: number,
    timeStampController: TimeStampController,
) => void;

export const RecordingThumbnail: FC<
    React.PropsWithChildren<{
        artifact: Recording;
        canSelect: boolean;
        isSelected: boolean;
        isSelectVisible: boolean;
        onSelect?: (artifactSid: string) => void;
        onPlay?: () => void;
        videoStyleOverrides?: CSSProperties;
        timeUpdateHandler: (event: Event) => void;
    }>
> = ({ artifact, ...props }) => {
    if (!artifact.recording) {
        log.error('Attempted to render non-recording artifact as a recording');
        return null;
    }

    return (
        <VideoThumbnail
            {...props}
            artifactSid={artifact.id}
            videoUrl={artifact.recording.downloadUrl}
        />
    );
};

export const RecordingTrackThumbnail: FC<
    React.PropsWithChildren<{
        artifact: Recording;
        canSelect: boolean;
        isSelected: boolean;
        isSelectVisible: boolean;
        onSelect?: (artifactSid: string) => void;
        onPlay?: () => void;
    }>
> = ({ artifact, ...props }) => (
    <VideoThumbnail
        {...props}
        artifactSid={artifact.id}
        videoUrl={artifact.recording.previewDownloadUrl}
    />
);

const VideoThumbnail: FC<
    React.PropsWithChildren<{
        videoUrl?: string;
        artifactSid: string;
        canSelect: boolean;
        isSelected: boolean;
        isSelectVisible: boolean;
        timeUpdateHandler?: (event: Event) => void;
        onSelect?: (artifactSid: string) => void;
        onPlay?: () => void;
        videoStyleOverrides?: CSSProperties;
    }>
> = ({
    videoUrl,
    artifactSid,
    canSelect,
    isSelected,
    isSelectVisible: isAllSelectsVisible,
    timeUpdateHandler,
    onSelect,
    onPlay,
    videoStyleOverrides = {},
}) => {
    const [isSelectVisible, setIsSelectVisible] = useState(false);
    const [isWrapperFocused, setIsWrapperFocused] = useState(false);
    const [isSelectFocused, setIsSelectFocused] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const timeStampController = useTimeStampController();
    const { initializeVideoElementRef } = useTranscriptionContext();

    useEffect(() => {
        if (!isMouseOver) {
            setIsSelectVisible(isAllSelectsVisible);
        }
    }, [isAllSelectsVisible, isMouseOver]);
    return (
        <RecordingWrapper
            onMouseOver={() => setIsMouseOver(true)}
            onMouseEnter={() => !isAllSelectsVisible && setIsSelectVisible(true)}
            onMouseLeave={() => {
                setIsMouseOver(false);
                !isAllSelectsVisible && setIsSelectVisible(false);
            }}
            onFocus={() => setIsWrapperFocused(true)}
            onBlur={() => setIsWrapperFocused(false)}
            aria-label={isAllSelectsVisible ? 'select artifact' : 'view artifact details'}
        >
            <VideoWithPlayButton
                onMount={(videoElement: HTMLVideoElement) => {
                    timeStampController.registerVideoElement(videoElement);
                    initializeVideoElementRef(videoElement);
                }}
                onPlay={onPlay}
                key={artifactSid}
                controls
                timeUpdateHandler={timeUpdateHandler}
                style={{
                    gridColumn: 'span 2',
                    width: '100%',
                    height: '100%',
                    maxHeight: '400px',
                    backgroundColor: '#000',
                    ...videoStyleOverrides,
                }}
                data-testid={`recording-${artifactSid}`}
                data-clarity-mask="true"
            >
                <source src={videoUrl} type="video/mp4" />
                <source src={videoUrl} type="video/3gpp" />
            </VideoWithPlayButton>
            {canSelect && (
                <>
                    <ThumbnailSelectBox
                        className={isSelectFocused ? 'focus-visible' : ''}
                        data-testid={`recording-select-button-${artifactSid}`}
                        isSelected={isSelected}
                        role="button"
                        onClick={() => onSelect?.(artifactSid)}
                        onKeyDown={(event: ReactKeyboardEvent) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSelect?.(artifactSid);
                            }
                        }}
                        tabIndex={isAllSelectsVisible ? -1 : 0}
                        onFocus={() => setIsSelectFocused(true)}
                        onBlur={() => setIsSelectFocused(false)}
                        isVisible={
                            isAllSelectsVisible ||
                            isSelectVisible ||
                            isWrapperFocused ||
                            isSelectFocused
                        }
                        aria-label={'select artifact'}
                    >
                        {isSelected && <AppIcon name="DoneIcon" color="white" />}
                    </ThumbnailSelectBox>
                    <ThumbnailSelectedOverlay
                        isSelected={isSelected}
                        isAllSelectsVisible={isAllSelectsVisible}
                        onClick={() => onSelect?.(artifactSid)}
                    />
                </>
            )}
        </RecordingWrapper>
    );
};

export const StreemshotThumbnail: FC<
    React.PropsWithChildren<{
        canClickTimeStamp: boolean;
        streemshot: WallItem.IStreemshot & { id: string; streemshotId?: string };
        canSelect: boolean;
        isSelected: boolean;
        isSelectVisible: boolean;
        onSelect: (artifactSid: string) => void;
        imageDataRef: MutableRefObject<{ [url: string]: StreemshotData }>;
        onClickTimeStamp?: OnClickTimeStamp;
    }>
> = ({
    canClickTimeStamp,
    streemshot,
    canSelect,
    isSelected,
    isSelectVisible: isAllSelectsVisible,
    onSelect,
    imageDataRef,
    onClickTimeStamp,
}) => {
    const history = useHistory();
    const detailSession = useDetailSession();
    const [isSelectVisible, setIsSelectVisible] = useState(false);
    const [isWrapperFocused, setIsWrapperFocused] = useState(false);
    const [isSelectFocused, setIsSelectFocused] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const { isMobile } = useMediaBreakpoint();

    useEffect(() => {
        if (!isMouseOver) {
            setIsSelectVisible(isAllSelectsVisible);
        }
    }, [isAllSelectsVisible, isMouseOver]);

    const streemshotId = useMemo(
        () => new StreemshotId(streemshot?.streemshotId || streemshot.id),
        [streemshot.id, streemshot?.streemshotId],
    );
    const [note] = useObservable(detailSession.note.getStreemshotNote(streemshotId));
    const { dataURL, aspectRatio } = useFetchStreemshotData(
        streemshot?.downloadUrl ?? '',
        imageDataRef,
    );

    if (!dataURL || !aspectRatio) {
        // FIXME: if the fetch fails we can get stuck in a permanent loading state - https://streempro.atlassian.net/browse/ST-6527
        return <Skeleton />;
    }

    return (
        <ThumbnailWrapper
            aspectRatio={aspectRatio}
            onMouseOver={() => setIsMouseOver(true)}
            onMouseEnter={() => !isAllSelectsVisible && setIsSelectVisible(true)}
            onMouseLeave={() => {
                setIsMouseOver(false);
                !isAllSelectsVisible && setIsSelectVisible(false);
            }}
            onFocus={() => setIsWrapperFocused(true)}
            onBlur={() => setIsWrapperFocused(false)}
            aria-label={isAllSelectsVisible ? 'select artifact' : 'view artifact details'}
        >
            <ThumbnailImage
                url={dataURL}
                data-testid={`streemshot-${streemshot.id}`}
                data-clarity-mask="true"
                onClick={() => {
                    history.push(
                        joinPaths(history.location.pathname, `/artifact/${streemshot.id}`),
                    );
                }}
                onKeyDown={(event: ReactKeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (isAllSelectsVisible) {
                            onSelect?.(streemshot.id);
                        } else {
                            history.push(
                                joinPaths(history.location.pathname, `/artifact/${streemshot.id}`),
                            );
                        }
                    }
                }}
                role="button"
                aria-label="open image details"
                tabIndex={0}
            />

            {(streemshot as StreemshotWithRevision)?.clientCreatedAt && (
                <TimeStamp
                    streemshot={streemshot as StreemshotWithRevision}
                    onClick={onClickTimeStamp}
                    canClickTimeStamp={canClickTimeStamp}
                />
            )}
            {note?.text && (
                <ThumbnailNotesOverlay>
                    <ThumbnailNotes
                        as="p"
                        color="light"
                        data-testid={`streemshot-notes-${streemshot.id}`}
                        data-clarity-mask="true"
                    >
                        {note.text}
                    </ThumbnailNotes>
                </ThumbnailNotesOverlay>
            )}
            {canSelect && (
                <>
                    <ThumbnailSelectBox
                        className={isSelectFocused ? 'focus-visible' : ''}
                        data-testid={`streemshot-select-button-${streemshot.id}`}
                        isSelected={isSelected}
                        role="button"
                        onClick={() => onSelect?.(streemshot.id)}
                        onKeyDown={(event: ReactKeyboardEvent) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSelect?.(streemshot.id);
                            }
                        }}
                        tabIndex={isAllSelectsVisible ? -1 : 0}
                        onFocus={() => setIsSelectFocused(true)}
                        onBlur={() => setIsSelectFocused(false)}
                        isVisible={
                            isAllSelectsVisible ||
                            isSelectVisible ||
                            isWrapperFocused ||
                            isSelectFocused
                        }
                        aria-label={'select artifact'}
                    >
                        {isSelected && <AppIcon name="DoneIcon" color="white" />}
                    </ThumbnailSelectBox>
                    {!isMobile && (
                        <ThumbnailSelectedOverlay
                            isSelected={isSelected}
                            isAllSelectsVisible={isAllSelectsVisible}
                            onClick={() => onSelect?.(streemshot.id)}
                        />
                    )}
                </>
            )}
        </ThumbnailWrapper>
    );
};

const defaultOnClickTimestamp: OnClickTimeStamp = (
    streemshotCreatedAtTime,
    timeStampController,
) => {
    timeStampController.jumpVideoToTimeStamp(streemshotCreatedAtTime);
};
interface TimeStampProps {
    streemshot: StreemshotWithRevision;
    onClick?: OnClickTimeStamp;
    canClickTimeStamp: boolean;
}

const TimeStamp: FC<React.PropsWithChildren<TimeStampProps>> = ({
    streemshot,
    onClick = defaultOnClickTimestamp,
    canClickTimeStamp,
}: TimeStampProps) => {
    const [hovered, setHovered] = useState(false);
    const timeStampController = useTimeStampController();
    const theme = useTheme();

    const handleMouseEnterOrLeave: MouseEventHandler<HTMLButtonElement> = e => {
        switch (e.type) {
            case 'mouseenter':
                if (timeStampController.hasVideoArtifact && canClickTimeStamp) {
                    setHovered(true);
                }
                break;
            case 'mouseleave':
                setHovered(false);
                break;
            default:
                break;
        }
    };

    const handleTimeStampClick = () => {
        if (!canClickTimeStamp) return;

        const streemshotCreatedAtTime =
            streemshot.clientCreatedAt.seconds.low + streemshot.clientCreatedAt.nanos / 1000000000;
        onClick(streemshotCreatedAtTime, timeStampController);
    };

    if (!timeStampController.hasVideoArtifact) {
        return null;
    }
    return (
        <TimeStampButton
            data-testid={`streemshot-timestamp-button-${streemshot.id}`}
            onMouseEnter={handleMouseEnterOrLeave}
            onMouseLeave={handleMouseEnterOrLeave}
            onClick={handleTimeStampClick}
            canClickTimeStamp={canClickTimeStamp}
        >
            <Tooltip
                width="230px"
                delay={800}
                showTooltip={hovered}
                placement="bottom"
                message="Go to the time in the call recording when this streemshot was taken."
            >
                <Row alignItems="center" justifyContent="center">
                    <TimeStampText hovered={hovered}>
                        <AppText size="large" color="white" style={{ fontSize: '16px' }}>
                            {timeStampController.getStreemShotTimeStamp(streemshot)}
                        </AppText>
                    </TimeStampText>
                    <Box pt={'4px'}>
                        <AppIcon
                            name="TimeStampArrowIcon"
                            size="medium"
                            color={theme.colors.white}
                        />
                    </Box>
                </Row>
            </Tooltip>
        </TimeStampButton>
    );
};

const TimeStampText = styled.p<{ hovered: boolean }>`
    position: relative;
    margin-right: 5px;
    border-bottom: 1px solid
        ${({ theme, hovered }) => (hovered ? theme.colors.white : 'transparent')};
`;
export const TimeStampButton = styled.button<{ canClickTimeStamp: boolean }>`
    position: absolute;
    border: 1px solid transparent;
    right: ${({ theme }) => theme.spacing.m};
    top: ${({ theme }) => theme.spacing.m};
    padding: 2px 8px 6px 8px;
    background: none;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.blackA40};
    &:hover {
        cursor: ${({ canClickTimeStamp }) => (canClickTimeStamp ? 'pointer' : 'default')};
        border: 1px solid
            ${({ theme, canClickTimeStamp }) => (canClickTimeStamp ? theme.colors.white : 'none')};
    }
`;

const RecordingWrapper = styled.div({
    position: 'relative',
    overflow: 'hidden',
    flexGrow: 0,
});

const ThumbnailWrapper = styled.div<{
    aspectRatio: number;
}>(({ aspectRatio }) => ({
    gridColumn: `span ${aspectRatio < 1 ? 1 : 2}`,
    position: 'relative',
    overflow: 'hidden',
    flexGrow: 1,
}));

const ThumbnailImage = styled.div<{
    url: string | undefined;
}>(({ url }) => ({
    backgroundImage: `url(${url})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    cursor: 'pointer',
    width: '100%',
    height: '100%',
}));

const ThumbnailNotesOverlay = styled.div(() => {
    const streemshotNotesFadeHeight = '128px';
    const streemshotNotesMaxOpacity = '0.75';
    return {
        background: `linear-gradient(180deg, rgba(0,0,0,0) 0%,
          rgba(0,0,0,${streemshotNotesMaxOpacity}) ${streemshotNotesFadeHeight},
          rgba(0,0,0,${streemshotNotesMaxOpacity}) 100%)`,
        padding: `${streemshotNotesFadeHeight} 16px 16px`,
        color: 'white',
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        overflow: 'hidden',
        pointerEvents: 'none',
    };
});

const ThumbnailNotes = styled(AppText)`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 60px;
`;

const ThumbnailSelectBox = styled.div<{ isSelected: boolean; isVisible: boolean }>(
    ({ theme, isSelected, isVisible }) => ({
        position: 'absolute',
        zIndex: 1,
        top: theme.spacing.m,
        left: theme.spacing.m,
        backgroundColor: isSelected ? theme.colors.azure : theme.colors.white,
        height: isVisible ? theme.spacing.l : 0,
        width: isVisible ? theme.spacing.l : 0,
        border:
            isSelected && isVisible
                ? `solid 1px ${theme.colors.white}`
                : isVisible
                ? `solid 1px ${theme.colors.azure}`
                : 'none',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        ':focus:focus:not(.focus-visible)': {
            outline: 'inherit',
            boxShadow: 'inherit',
        },
    }),
);

const ThumbnailSelectedOverlay = styled.div<{
    isSelected: boolean;
    isAllSelectsVisible: boolean;
}>(({ isSelected, isAllSelectsVisible, theme }) => ({
    pointerEvents: isAllSelectsVisible ? 'auto' : 'none',
    cursor: isAllSelectsVisible ? 'pointer' : 'auto',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    border: isSelected ? `${theme.spacing.s} solid ${theme.colors.azureTransparent}` : '',
}));

const VideoWithPlayButton: FC<
    React.PropsWithChildren<{
        key: string;
        style: { [key: string]: string | number };
        controls: boolean;
        onPlay?: () => void;
        onMount?: (video: HTMLVideoElement) => void;
        timeUpdateHandler?: (event: Event) => void;
    }>
> = ({ children, onPlay, onMount, timeUpdateHandler, ...props }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [showOverlay, setShowOverlay] = useState(true);

    useEffect(() => {
        const handlePlay = () => {
            setShowOverlay(false);
            onPlay?.();
        };
        const handlePause = () => {
            setShowOverlay(true);
        };
        const videoEl = videoRef.current;

        if (onMount) {
            onMount(videoEl);
        }

        if (videoEl) {
            videoEl.addEventListener('play', handlePlay);
            videoEl.addEventListener('pause', handlePause);
            timeUpdateHandler && videoEl.addEventListener('timeupdate', timeUpdateHandler);
        }
        return () => {
            if (videoEl) {
                videoEl.removeEventListener('play', handlePlay);
                videoEl.removeEventListener('pause', handlePause);
                timeUpdateHandler && videoEl.removeEventListener('timeupdate', timeUpdateHandler);
            }
        };
    }, [videoRef, setShowOverlay, onMount, onPlay, timeUpdateHandler]);

    return (
        <>
            {
                // We don't have a captions track
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                    {...props}
                    style={{ ...props.style, position: 'relative' }}
                    ref={videoRef}
                    crossOrigin="anonymous"
                >
                    {children}
                </video>
            }
            <PlayButton
                data-testid="video-play-button-overlay"
                className={showOverlay ? 'fade-in' : 'fade-out'}
                onClick={() => videoRef.current!.play()}
            >
                <AppIcon name="PlayArrowIcon" />
            </PlayButton>
        </>
    );
};

const PlayButton = styled('button')`
    position: absolute;
    top: 50%;
    right: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
    width: 80px;
    border: 3px solid ${props => props.theme.colors.white};
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding-left: 15px;
    transform: translate(50%, -50%);
    cursor: pointer;

    &.fade-out {
        visibility: hidden;
        opacity: 0;
        transition: visibility 0s linear 300ms, opacity 300ms;
    }
    &.fade-in {
        visibility: visible;
        opacity: 1;
        transition: visibility 0s linear 0s, opacity 300ms;
    }
`;
